import { Box, DialogContent, Stack } from '@mui/material';
import { type FormEvent, type KeyboardEvent, useEffect, useRef } from 'react';
import { ModalContainerFooter } from './modal-container-footer';
import { ModalContainerForm } from './modal-container-form';
import { ModalContainerHeader } from './modal-container-header';
import { modalBodySx, modalScrollAreaSx, modalStepSx } from './modal-container.styles';
import type { ModalStepProps } from './modal-container.types';
import {
  getInitialFocusTarget,
  getPrimaryActionButton,
  isSubmitAction,
  MODAL_BODY_ATTRIBUTE,
} from './modal-container.utils';
import { useModalContainerContext, useModalStepContext } from './modal-steps-context';
import { useModalScrollState } from './use-modal-scroll-state';

/** One step of a ModalContainer, opened with useModalSteps().pushStep; also a wizard's first step. */
export const ModalStep = ({
  title,
  titleTestId,
  testId,
  headerActions,
  actions,
  children,
}: ModalStepProps) => {
  const { titleId, onClose, closeBlockedRef } = useModalContainerContext();
  const { index, isTop, popStep } = useModalStepContext();
  const submitAction = actions?.find(isSubmitAction);
  const isSaving = actions?.some(({ loading }) => loading) ?? false;
  const { scrollAreaRef, contentRef, onScroll, isScrolled, isScrollable } =
    useModalScrollState();
  const stepRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (stepRef.current) {
      getInitialFocusTarget(stepRef.current)?.focus();
    }
  }, []);

  useEffect(() => {
    const step = stepRef.current;
    // A loading primary is disabled, which drops focus onto the dialog root; once a save that
    // kept the dialog open ends (an error), put focus back on the primary.
    if (!isSaving && isTop && step && !step.contains(document.activeElement)) {
      getPrimaryActionButton(step)?.focus();
    }
  }, [isSaving, isTop]);

  // The Dialog's Escape and backdrop handlers live on the container, while `loading` lives in the
  // top step's actions. Lifting it into container state would mean a child notifying its parent
  // from an effect, so the top step publishes the flag to a ref the handlers read at event time.
  useEffect(() => {
    if (!isTop) {
      return undefined;
    }
    closeBlockedRef.current = isSaving;
    return () => {
      closeBlockedRef.current = false;
    };
  }, [closeBlockedRef, isTop, isSaving]);

  const handleClose = () => {
    if (!isSaving) {
      onClose();
    }
  };

  const handleBack =
    index > 0
      ? () => {
          if (!isSaving) {
            popStep?.();
          }
        }
      : undefined;

  const handleSubmit = (event: FormEvent) => {
    // A form nested in the body bubbles its submit through React; it must not fire the primary too.
    if (event.target !== event.currentTarget) {
      return;
    }
    event.preventDefault();
    if (submitAction && !submitAction.disabled && !submitAction.loading) {
      submitAction.onClick?.();
    }
  };

  // Capture phase: a rich-text editor binds Mod-Enter to a hard break and would swallow it first.
  const handleKeyDownCapture = (event: KeyboardEvent) => {
    const step = stepRef.current;
    const isSubmitChord =
      event.key === 'Enter' &&
      (event.metaKey || event.ctrlKey) &&
      !event.nativeEvent.isComposing;
    if (isSubmitChord && step instanceof HTMLFormElement) {
      event.preventDefault();
      event.stopPropagation();
      step.requestSubmit();
    }
  };

  return (
    <Stack
      ref={stepRef}
      component={submitAction ? ModalContainerForm : 'div'}
      onSubmit={submitAction ? handleSubmit : undefined}
      onKeyDownCapture={submitAction ? handleKeyDownCapture : undefined}
      sx={modalStepSx}
      data-testid={testId}
    >
      <ModalContainerHeader
        title={title}
        titleId={isTop ? titleId : undefined}
        titleTestId={titleTestId}
        onClose={handleClose}
        onBack={handleBack}
        headerActions={headerActions}
        isScrolled={isScrolled}
        isSaving={isSaving}
      />
      <DialogContent ref={scrollAreaRef} onScroll={onScroll} sx={modalScrollAreaSx}>
        <Box ref={contentRef} sx={modalBodySx} {...{ [MODAL_BODY_ATTRIBUTE]: true }}>
          {children}
        </Box>
      </DialogContent>
      {actions ? (
        <ModalContainerFooter
          actions={actions}
          onDismiss={handleBack ?? handleClose}
          isScrollable={isScrollable}
          isSaving={isSaving}
        />
      ) : null}
    </Stack>
  );
};
